import { MaiFeedbackRating } from '~/lib/types/mai/MaiFeedbackRating';
import { MaiFeedbackReason } from '~/lib/types/mai/MaiFeedbackReason';

import { dedupeCitations } from './maiChat.util';
import {
  MaiMessage,
  MaiMessageSegment,
  MaiToolCallStatus,
} from './maiChat.types';

export interface MaiHistoryFeedback {
  rating: MaiFeedbackRating;
  reason: MaiFeedbackReason | null;
  comment: string | null;
}

export interface MaiHistoryToolCall {
  name: string;
  status: MaiToolCallStatus;
}

export interface MaiHistoryToolResult {
  toolUseId: string;
  status: MaiToolCallStatus;
  sources: { file: string; page: number | null; url: string }[];
}

export interface MaiHistoryRow {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  tool_calls: MaiHistoryToolCall[] | null;
  tool_results: MaiHistoryToolResult[] | null;
  created_at: string;
  feedback?: MaiHistoryFeedback | null;
}

const RUN_SEAM = /([a-z\d)\]"'])([.!?])(?=[A-Z*#])/g;

const splitConcatenatedRuns = (content: string): string =>
  content.replace(RUN_SEAM, '$1$2\n\n');

const buildHistorySegments = (row: MaiHistoryRow): MaiMessageSegment[] => {
  const segments: MaiMessageSegment[] = [];
  const hasToolCalls = Boolean(row.tool_calls && row.tool_calls.length > 0);
  if (hasToolCalls) {
    segments.push({
      kind: 'tools',
      toolCalls: row.tool_calls.map((toolCall, index) => ({
        toolUseId: `${row.id}-tc-${index}`,
        name: toolCall.name,
        status: toolCall.status,
      })),
    });
  }
  if (row.content.length > 0) {
    segments.push({
      kind: 'text',
      text: hasToolCalls ? splitConcatenatedRuns(row.content) : row.content,
    });
  }
  return segments;
};

export const mapHistoryToMessages = (rows: MaiHistoryRow[]): MaiMessage[] =>
  rows.reduce<MaiMessage[]>((messages, row) => {
    if (row.role === 'user') {
      messages.push({
        id: row.id,
        role: 'user',
        status: 'sent',
        createdAt: row.created_at,
        text: row.content,
      });
    } else if (row.role === 'assistant') {
      const sources = (row.tool_results ?? []).flatMap(
        (result) => result.sources,
      );
      const citations = dedupeCitations(sources);
      messages.push({
        id: row.id,
        role: 'ai',
        status: 'sent',
        createdAt: row.created_at,
        messageId: row.id,
        segments: buildHistorySegments(row),
        ...(citations.length > 0 && { citations }),
        ...(row.feedback && { rating: row.feedback.rating }),
      });
    }
    return messages;
  }, []);
